import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCheckoutContextState,
  getOrInitCheckoutSession,
  trackMixpanel,
} from '@monorepo/client-common';
import { PagePath } from '@monorepo/client-types';
import { SystemIcon } from '@monorepo/nova-icons';
import {
  CartData,
  CartStep,
  CheckoutEntryPath,
  CheckoutSource,
  EventTypes,
  isMetadataFromType,
  OptionalAssets,
  ProductType,
} from '@monorepo/types';
import { getActiveAssets } from '../../libs/const/cart-items';
import { getFilteredActiveAssets } from '../../utils';
import { calculateConvertedPrice } from '../../utils/price-utils';
import { StyledCartCloseButton, StyledCartContainer } from './cart.styled';
import { CartAssetTabs } from './cart-asset-tabs';
import { CartCheckoutButton } from './cart-checkout-button';
import { CartContent } from './cart-content';
import { CartEmptyState } from './cart-empty-state';
import { CartHeader } from './cart-header';

export const CartPopover = ({
  handleClose,
  cartData,
}: {
  handleClose: () => void;
  cartData: CartData;
}) => {
  const navigate = useNavigate();
  const { cart, products, currencies, project } = cartData;
  const activeAssets = cart ? getActiveAssets(cart) : [];
  const filteredActiveAssets = project
    ? getFilteredActiveAssets(activeAssets, project)
    : [];
  const defaultAsset = filteredActiveAssets[0] || undefined;

  const [selectedAsset, setSelectedAsset] =
    useState<OptionalAssets>(defaultAsset);

  useEffect(() => {
    if (!selectedAsset || !filteredActiveAssets.includes(selectedAsset)) {
      setSelectedAsset(defaultAsset);
    }
  }, [cart, project]);

  // Show empty state when cartData is missing (loading/error) or when there are no active assets
  if (!cart || !products || !currencies || filteredActiveAssets.length === 0) {
    return <CartEmptyState onClose={handleClose} />;
  }

  const handleCheckoutClick = () => {
    handleClose();
    if (selectedAsset) {
      const checkoutSessionId = getOrInitCheckoutSession(
        CheckoutEntryPath.ResumeExplicit,
        CheckoutSource.CartWidget,
      );

      const cartAssetData = cart[selectedAsset];
      const firstItem = cartAssetData?.items?.[0];
      const programMetadata =
        firstItem && isMetadataFromType(firstItem, ProductType.Program)
          ? firstItem.metadata
          : null;

      const itemCount = cartAssetData?.items?.length ?? 0;
      trackMixpanel({
        event: EventTypes.CHECKOUT_RESUME_CLICKED,
        properties: {
          asset: selectedAsset,
          group_name: programMetadata?.groupName || 'na',
          buying_power: programMetadata?.buyingPower || 0,
          trading_system: programMetadata?.tradingSystem || 'na',
          total_amount_usd: programMetadata?.priceUsd || 0,
          total_amount_local: calculateConvertedPrice(
            cartAssetData?.totalPrice || 0,
            cart.currency,
            currencies,
          ),
          currency: cart.currency,
          checkout_entry_path: CheckoutEntryPath.ResumeExplicit,
          checkout_source: CheckoutSource.CartWidget,
          checkout_session_id: checkoutSessionId,
          checkout_context_state: getCheckoutContextState(itemCount),
        },
      });

      const cartUrl = `${PagePath.Purchase}?step=${CartStep.Billing}&asset=${selectedAsset}`;
      navigate(cartUrl);
    }
  };

  return (
    <StyledCartContainer>
      {/* Close button - absolute positioned top-right */}
      <StyledCartCloseButton size="small" onClick={handleClose}>
        <SystemIcon iconName="Close" size={16} />
      </StyledCartCloseButton>

      <CartHeader />

      {/* Asset tabs */}
      {filteredActiveAssets.length > 1 && (
        <CartAssetTabs
          cart={cart}
          assets={filteredActiveAssets}
          selectedAsset={selectedAsset}
          onAssetChange={setSelectedAsset}
        />
      )}

      {/* Cart content */}
      {selectedAsset && (
        <CartContent cartData={cartData} selectedAsset={selectedAsset} />
      )}

      <CartCheckoutButton
        onClick={handleCheckoutClick}
        disabled={!selectedAsset}
      />
    </StyledCartContainer>
  );
};
